import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

// ============================================================================
// HELPER FUNCTIONS - Defined outside component to prevent recreationon every render
// ============================================================================

// Function to remove ONLY ** characters while preserving collapsible sections and markdown
const ensureCleanAIResponse = (content) => {
  if (!content) return "";

  let cleaned = content
    .replace(/\*\*(?![^*]*\*\*)/g, "")
    .replace(/(?<!\*)\*\*(?!\*)/g, "")
    .replace(/\*\*/g, "");

  return cleaned;
};

// Function to clean markdown while preserving HTML tags
const cleanMarkdownPreservingHTML = (content) => {
  if (!content) return "";

  if (content.includes("<") && content.includes(">")) {
    return content
      .replace(/^#{1,6}\s*/gm, "")
      .replace(/👉\s*([^(]+)\(click\s+to\s+expand\)/gi, "$1")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/__(.*?)__/g, "$1")
      .replace(/\*\*/g, "")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/_(.*?)_/g, "$1")
      .replace(/\*/g, "")
      .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, "").trim())
      .replace(/`([^`]+)`/g, "$1")
      .replace(/~~(.*?)~~/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/^[-*_]{3,}$/gm, "")
      .replace(/\n\s*\n\s*\n/g, "\n\n")
      .trim();
  }

  return content;
};

// Function to render content with HTML support
const renderContentWithHTML = (content) => {
  if (!content) return null;

  let cleanedContent = ensureCleanAIResponse(content);

  if (
    content.includes("<details>") ||
    content.includes("<summary>") ||
    content.includes("</details>")
  ) {
    let htmlCleanedContent = cleanMarkdownPreservingHTML(content);
    htmlCleanedContent = ensureCleanAIResponse(htmlCleanedContent);
    return <div dangerouslySetInnerHTML={{ __html: htmlCleanedContent }} />;
  }

  return <div className="message-text">{cleanedContent}</div>;
};

// Function to parse AI responses into collapsible sections
const parseCollapsibleContent = (content) => {
  if (!content) return null;

  const cleanedContent = ensureCleanAIResponse(content);
  const headerRegex = /^(#{2,6}\s+.+)$/gm;
  const explanationRegex = /👉\s*[^(]+\(click\s+to\s+expand\)/gi;
  const headers = cleanedContent.match(headerRegex);
  const explanations = cleanedContent.match(explanationRegex);

  if (
    (!headers || headers.length === 0) &&
    (!explanations || explanations.length === 0)
  ) {
    return { type: "regular", content: cleanedContent };
  }

  const collapsibleSections = [];
  const lines = cleanedContent.split("\n");
  let currentSection = null;
  let sectionIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.match(/^#{2,6}\s+/)) {
      if (currentSection) {
        collapsibleSections.push(currentSection);
      }

      const headerText = line.replace(/^#{2,6}\s+/, "");
      currentSection = {
        id: `section-${sectionIndex}`,
        header: headerText,
        content: "",
        isExpanded: false,
      };
      sectionIndex++;
    } else if (line.match(/👉\s*[^(]+\(click\s+to\s+expand\)/gi)) {
      if (currentSection) {
        collapsibleSections.push(currentSection);
      }

      const match = line.match(/👉\s*([^(]+)\(click\s+to\s+expand\)/i);
      const headerText = match ? match[1].trim() : "Details";

      currentSection = {
        id: `section-${sectionIndex}`,
        header: headerText,
        content: "",
        isExpanded: false,
      };
      sectionIndex++;
    } else if (currentSection && line) {
      currentSection.content += (currentSection.content ? "\n" : "") + line;
    }
  }

  if (currentSection) {
    collapsibleSections.push(currentSection);
  }

  return { type: "collapsible", sections: collapsibleSections };
};

// ============================================================================
// COLLAPSIBLE CONTENT COMPONENT - Defined outside to prevent recreation
// ============================================================================

const CollapsibleContent = React.memo(
  ({ content, messageIndex, sections, onToggle }) => {
    const cleanedContent = useMemo(() => {
      return ensureCleanAIResponse(content);
    }, [content]);

    const parsedContent = useMemo(() => {
      return parseCollapsibleContent(cleanedContent);
    }, [cleanedContent]);

    if (!parsedContent) return null;

    if (parsedContent.type === "regular") {
      return renderContentWithHTML(parsedContent.content);
    }

    const displaySections = sections || parsedContent.sections;

    return (
      <div className="collapsible-content">
        {displaySections.map((section) => (
          <div key={section.id} className="collapsible-section">
            <div
              className="collapsible-header"
              onClick={() => onToggle(messageIndex, section.id)}
            >
              <span className="section-title-ai">{section.header}</span>
              <span
                className={`expand-icon ${
                  section.isExpanded ? "expanded" : ""
                }`}
              >
                {section.isExpanded ? "▼" : "▶"}
              </span>
            </div>
            {section.isExpanded && (
              <div className="collapsible-body">
                <div className="section-content">
                  {renderContentWithHTML(section.content)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AIAssistant = ({
  sessionId,
  selectedFile,
  fileContent,
  files,
  originalFiles,
  analysisResults,
  isFullScreen = false,
  chatHistory,
  isLoading,
  isAvailable,
  onSendMessage,
  onClearChat,
  onOpenExtendedAI,
  onCollapse,
  onClose,
}) => {
  // Internal state for user input
  const [userInput, setUserInput] = useState("");

  // State for managing collapsible sections in AI responses
  const [collapsibleSections, setCollapsibleSections] = useState({});

  // State for new message indicator
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const chatMessagesRef = useRef(null);
  const lastReadMessageCount = useRef(chatHistory.length);

  // Show indicator only when NEW message arrives while scrolled up
  useEffect(() => {
    const element = chatMessagesRef.current;
    if (!element || chatHistory.length === 0) return;

    // Check if this is a new message (count increased)
    if (chatHistory.length > lastReadMessageCount.current) {
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;

      if (isNearBottom) {
        // User is at bottom, they'll see it automatically - mark as read
        lastReadMessageCount.current = chatHistory.length;
        setHasUnreadMessages(false);
      } else {
        // User is scrolled up, show indicator
        setHasUnreadMessages(true);
      }
    }
  }, [chatHistory.length]);

  // Hide indicator when user scrolls to bottom
  useEffect(() => {
    const element = chatMessagesRef.current;
    if (!element) return;

    const checkScrollPosition = () => {
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;

      if (isNearBottom && hasUnreadMessages) {
        // User scrolled to bottom, mark messages as read
        lastReadMessageCount.current = chatHistory.length;
        setHasUnreadMessages(false);
      }
    };

    element.addEventListener('scroll', checkScrollPosition);
    return () => element.removeEventListener('scroll', checkScrollPosition);
  }, [hasUnreadMessages, chatHistory.length]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    const element = chatMessagesRef.current;
    if (element) {
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth'
      });
      // Will be marked as read by scroll event listener
    }
  }, []);

  // AI availability is now managed by parent component

  // All chat functions are now managed by parent component

  // Initialize collapsible sections when chat history changes
  useEffect(() => {
    setCollapsibleSections((prev) => {
      let updated = { ...prev };
      let hasChanges = false;

      chatHistory.forEach((message, index) => {
        if (message.role === "assistant" && !updated[index]) {
          const cleanedContent = ensureCleanAIResponse(message.content);
          const parsed = parseCollapsibleContent(cleanedContent);

          if (parsed?.type === "collapsible") {
            updated[index] = parsed.sections;
            hasChanges = true;
          }
        }
      });

      // Only update if there were actual changes
      return hasChanges ? updated : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory]);

  // Function to toggle section expansion
  const toggleSection = useCallback((messageIndex, sectionId) => {
    console.log("🔍 toggleSection called:", { messageIndex, sectionId });
    setCollapsibleSections((prev) => {
      console.log("📊 Previous state:", prev);
      const currentSections = prev[messageIndex];
      console.log(
        "📋 Current sections for message",
        messageIndex,
        ":",
        currentSections
      );

      if (!currentSections) {
        console.log("❌ No sections found for message", messageIndex);
        return prev;
      }

      const updatedSections = currentSections.map((section) => {
        if (section.id === sectionId) {
          console.log(
            "✅ Toggling section:",
            section.id,
            "from",
            section.isExpanded,
            "to",
            !section.isExpanded
          );
          return { ...section, isExpanded: !section.isExpanded };
        }
        return section;
      });

      const newState = {
        ...prev,
        [messageIndex]: updatedSections,
      };

      console.log("🆕 New state:", newState);
      return newState;
    });
  }, []);

  // Handle sending message
  const handleSendMessage = useCallback(() => {
    if (!userInput.trim() || !sessionId) return;

    // Send the message to parent
    onSendMessage(userInput.trim());

    // Clear the input
    setUserInput("");
  }, [userInput, sessionId, onSendMessage]);

  // Handle key press in textarea
  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  return (
    <div className={`ai-assistant ${isFullScreen ? "full-screen" : ""}`}>
      {isFullScreen && (
        <div className="ai-branding-header">
          <div className="ai-branding-left">
            <div className="ai-logo-container">
              <img
                src="/ai-logo.png"
                alt="AI Assistant"
                width="32"
                height="32"
              />
            </div>
            <div className="ai-branding-text">
              <h2 className="ai-brand-title">Coretix IAI</h2>
              <p className="ai-brand-subtitle">Questions? Ask me .</p>
            </div>
          </div>
          {onClose && (
            <button
              className="ai-overlay-close-button"
              onClick={onClose}
              title="Close AI Assistant"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {!isFullScreen && (
        <div className="ai-header">
          <div className="ai-header-left">
            <img src="/ai-logo.png" alt="AI Assistant" width="36" height="36" />
          </div>

          <div className="ai-header-right">
            {onOpenExtendedAI && (
              <button
                className="ai-expand-button"
                onClick={onOpenExtendedAI}
                title="Open extended AI Assistant"
              >
                <img
                  src="/expand.svg"
                  alt="Expand"
                  width="16"
                  height="16"
                  style={{ color: "white" }}
                />
              </button>
            )}
            {onCollapse && (
              <button
                className="ai-close-button"
                onClick={onCollapse}
                title="Collapse AI Assistant"
              >
                <img
                  src="/close.svg"
                  alt="Close"
                  width="16"
                  height="16"
                  style={{ color: "white" }}
                />
              </button>
            )}
          </div>
        </div>
      )}

      {!isFullScreen && (
        <div className="ai-context">
          {selectedFile && (
            <div className="context-info">
              <span className="context-file" title={selectedFile}>
                {" "}
                {selectedFile}
              </span>
              <span className="context-size">
                {/* {fileContent ? `${fileContent.length} chars` : 'No content'} */}
              </span>
            </div>
          )}
          {analysisResults && (
            <div className="context-info">
              <span className="context-file">Analysis Results Available</span>
              <span className="context-size">
                {analysisResults.summary?.total_functions
                  ? `${analysisResults.summary.total_functions} functions analyzed`
                  : "Analysis completed"}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="chat-container">
        <div className="chat-messages" ref={chatMessagesRef}>
          {chatHistory.length === 0 ? (
            <div className="welcome-message">
              <p>👋 Hi! I'm your AI assistant with conversational memory.</p>

              {!isAvailable && (
                <div className="warning-message">
                  ⚠️ AI service is not available. Please check your OpenAI API
                  key configuration.
                </div>
              )}
            </div>
          ) : (
            chatHistory.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                {message.role === "assistant" && isFullScreen && (
                  <div className="message-avatar-badge">
                    <span className="avatar-badge-text">IAI</span>
                  </div>
                )}

                <div
                  className={
                    isFullScreen ? "message-body-fullscreen" : "message-header"
                  }
                >
                  {!isFullScreen && (
                    <>
                      <span className="message-role">
                        {message.role === "user" ? "👤 You" : "🤖 AI"}
                      </span>

                      <span className="message-time">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                    </>
                  )}
                </div>

                <div className="message-content">
                  {message.role === "assistant" ? (
                    <CollapsibleContent
                      content={message.content}
                      messageIndex={index}
                      sections={collapsibleSections[index]}
                      onToggle={toggleSection}
                    />
                  ) : (
                    <div className="message-text">{message.content}</div>
                  )}
                </div>

                {message.analysis && (
                  <div className="message-analysis">
                    <details>
                      <summary> Analysis Details</summary>

                      <pre>{JSON.stringify(message.analysis, null, 2)}</pre>
                    </details>
                  </div>
                )}
              </div>
            ))
          )}

          {isLoading && (
            <div className="message assistant">
              <div className="message-header">
                <span className="message-role">🤖 AI</span>
              </div>

              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>

                  <span></span>

                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating New Message Indicator */}
        {hasUnreadMessages && (
          <button
            className="new-message-indicator"
            onClick={scrollToBottom}
            title="Scroll to latest message"
          >
            <span className="indicator-text">New messages</span>
            <span className="indicator-arrow">↓</span>
          </button>
        )}
      </div>

      {/* Chat Input */}
      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            className="chat-input"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your Pl/I"
            disabled={isLoading || !isAvailable}
          />
          <button
            className="chat-send-icon"
            onClick={handleSendMessage}
            disabled={!userInput.trim() || isLoading || !sessionId}
            title="Send message"
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              <svg
                className="send-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </div>
        <button
          className="clear-chat-button"
          onClick={onClearChat}
          disabled={chatHistory.length === 0}
        >
          Clear Chat
        </button>
      </div>
    </div>
  );
};

export default AIAssistant;
